import api from './axios';
import {
    AccountingLedgerFilterOptionsResponse,
    ApLedgerRecord,
    ApRecurringEvidenceOverrideRequest,
    ApRecurringOverrideRequest,
    ApVendorDetailResponse,
    ApVendorRecord,
    ArManualLinkApplyRequest,
    ArManualLinkApplyResponse,
    ArManualLinkCandidatesResponse,
    ArCustomerDetailResponse,
    ArCustomerRecord,
    ArManualAllocationApplyRequest,
    ArManualAllocationApplyResponse,
    ArManualInvoiceAllocationApplyRequest,
    ArInvoiceRecord,
    ArLedgerDetailResponse,
    ArLedgerRecord,
    BillPaymentMatchPayload,
    InventoryLinkedDocumentResponse,
    InventoryObservationsResponse,
    LedgerDatasetFilterParams,
    PaginatedResponse,
    QboBillRecord,
    QboGeneralLedgerRecord,
    QboInvoiceRecord,
    QboTransactionListItem,
    UploadedLedgerRecord,
    VendorLineItemRecord
} from './types';

export interface ArWeeklyMetricRecord {
    business_id: string;
    week_start: string;
    week_end: string;
    actual_revenue_linked_cash?: number;
    expected_cash_due?: number;
    landed_expected_cash?: number;
    missed_expected_cash_amount?: number;
    missed_expected_receipt_count?: number;
    open_ar?: number;
    total_overdue_amount?: number;
    new_overdue_amount?: number;
    unapplied_cash_amount?: number;
    partial_payment_amount?: number;
    updated_at?: string;
}

export interface ArWeeklyActionRecord {
    business_id: string;
    week_start?: string;
    action_type?: string;
    title?: string;
    details?: string;
    description?: string;
    priority?: string;
    amount?: number;
    status?: string;
    customer_id?: string;
    customer_name?: string;
    invoice_id?: string;
    invoice_number?: string;
}

export interface ArWeeklyRevenueContributionRecord {
    business_id: string;
    week_start?: string;
    week_end?: string;
    contribution_id?: string;
    cash_application_id?: string;
    payment_id?: string;
    bank_receipt_id?: string;
    invoice_id?: string;
    customer_id?: string;
    customer_name?: string;
    invoice_number?: string;
    event_date?: string;
    allocated_amount?: number;
    allocation_type?: string;
    match_method?: string;
    confidence_score?: number;
    receipt_classification?: string;
    receipt_outcome?: string;
    receipt_review_status?: string;
    included_in_weekly_revenue?: boolean;
    exclusion_reason?: string | null;
    trigger_source?: string;
    updated_at?: string;
}

export type LedgerDatasetItem =
    | ApLedgerRecord
    | ApVendorRecord
    | ArCustomerRecord
    | QboTransactionListItem
    | QboGeneralLedgerRecord
    | QboInvoiceRecord
    | QboBillRecord
    | ArLedgerRecord
    | UploadedLedgerRecord
    | VendorLineItemRecord;

const ledgerViewsApi = {
    async refreshAccounting(businessId?: string): Promise<any> {
        try {
            const response = await api.post('/accounting/refresh', {
                sync_qbo: true,
                refresh_ar: true,
                refresh_ap: true,
                refresh_cache: true,
            }, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to queue accounting refresh:', error);
            throw error;
        }
    },

    async getLedgerItems(params: LedgerDatasetFilterParams): Promise<PaginatedResponse<LedgerDatasetItem>> {
        try {
            const response = await api.get<PaginatedResponse<LedgerDatasetItem>>('/accounting/ledger/items', {
                params,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch ledger dataset items:', error);
            throw error;
        }
    },

    async getFilterOptions(params: { source: 'transaction_list' | 'general_ledger' | 'ar_customers' | 'ar_ledger' | 'ap_vendors' | 'ap_ledger' | 'inventory'; business_id?: string }): Promise<AccountingLedgerFilterOptionsResponse> {
        try {
            const response = await api.get<AccountingLedgerFilterOptionsResponse>('/accounting/ledger/filter-options', {
                params,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch ledger filter options:', error);
            throw error;
        }
    },

    async getInventoryObservations(catalogId: string, businessId?: string): Promise<InventoryObservationsResponse> {
        try {
            const response = await api.get<InventoryObservationsResponse>(`/accounting/ledger/inventory-observations/${catalogId}`, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch inventory observations:', error);
            throw error;
        }
    },

    async getInventoryLinkedDocument(documentRef: string, params?: { business_id?: string; preferred_source?: 'uploaded_invoices' | 'uploaded_expenses' | 'uploaded_bills' | null }): Promise<InventoryLinkedDocumentResponse> {
        try {
            const response = await api.get<InventoryLinkedDocumentResponse>(`/accounting/ledger/document/${documentRef}`, {
                params: {
                    business_id: params?.business_id,
                    preferred_source: params?.preferred_source || undefined,
                },
            });
            return response.data;
        } catch (error) {
            console.error('Failed to resolve linked inventory document:', error);
            throw error;
        }
    },

    async getBillPaymentMatch(billId: string, businessId?: string): Promise<BillPaymentMatchPayload> {
        try {
            const response = await api.get<BillPaymentMatchPayload>(`/accounting/ledger/bills/${billId}/payment-match`, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch bill payment match:', error);
            throw error;
        }
    },

    async getArCustomerDetail(customerId: string, businessId?: string): Promise<ArCustomerDetailResponse> {
        try {
            const response = await api.get<ArCustomerDetailResponse>(`/accounting/ar/customers/${customerId}`, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch AR customer detail:', error);
            throw error;
        }
    },

    async getApVendorDetail(vendorId: string, businessId?: string): Promise<ApVendorDetailResponse> {
        try {
            const response = await api.get<ApVendorDetailResponse>(`/accounting/ap/vendors/${vendorId}`, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch AP vendor detail:', error);
            throw error;
        }
    },

    async getApSummary(businessId?: string): Promise<any> {
        try {
            const response = await api.get('/accounting/ap/summary', {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch AP summary:', error);
            throw error;
        }
    },

    async updateApExpectedPaymentDate(
        obligationId: string,
        expectedPaymentDate: Date | null,
        businessId?: string,
    ): Promise<{
        ap_obligation_id: string;
        expected_payment_date?: string | null;
        user_expected_payment_date?: string | null;
        date_source?: string | null;
        status?: string | null;
        updated_at?: string | null;
    }> {
        try {
            const response = await api.post(`/accounting/ap/obligations/${obligationId}/expected-payment-date`, {
                expected_payment_date: expectedPaymentDate ? expectedPaymentDate.toISOString().slice(0, 10) : null,
            }, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to update AP expected payment date:', error);
            throw error;
        }
    },

    async updateApVendorAccountingAccount(
        vendorId: string,
        standardAccountId: string,
        businessId?: string,
    ): Promise<{
        vendor_id?: string;
        vendor_name?: string;
        primary_accounting_account_id?: string | null;
        primary_accounting_account_name?: string | null;
        primary_accounting_category?: string | null;
        classification_source?: string | null;
    }> {
        try {
            const response = await api.post(`/accounting/ap/vendors/${vendorId}/accounting-account`, {
                standard_account_id: standardAccountId,
            }, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to update AP vendor accounting account:', error);
            throw error;
        }
    },

    async upsertApRecurringOverride(
        payload: ApRecurringOverrideRequest,
        businessId?: string,
    ): Promise<any> {
        try {
            const response = await api.post('/accounting/ap/recurring/override', payload, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to update AP recurring override:', error);
            throw error;
        }
    },

    async upsertApRecurringEvidenceOverride(
        payload: ApRecurringEvidenceOverrideRequest,
        businessId?: string,
    ): Promise<any> {
        try {
            const response = await api.post('/accounting/ap/recurring/evidence-override', payload, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to update AP recurring evidence override:', error);
            throw error;
        }
    },

    async getApRecurringOverrides(businessId?: string): Promise<any> {
        try {
            const response = await api.get('/accounting/ap/recurring/overrides', {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch AP recurring overrides:', error);
            throw error;
        }
    },

    async deleteApRecurringOverride(overrideId: string, businessId?: string): Promise<any> {
        try {
            const response = await api.delete(`/accounting/ap/recurring/override/${overrideId}`, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to delete AP recurring override:', error);
            throw error;
        }
    },

    async getArOpenInvoices(params?: { business_id?: string; status?: string; page?: number; page_size?: number }): Promise<PaginatedResponse<ArInvoiceRecord>> {
        try {
            const response = await api.get<PaginatedResponse<ArInvoiceRecord>>('/accounting/ar/open-invoices', {
                params: {
                    business_id: params?.business_id,
                    status: params?.status,
                    page: params?.page ?? 1,
                    page_size: params?.page_size ?? 200,
                },
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch AR open invoices:', error);
            throw error;
        }
    },

    async getArLedgerDetail(ledgerRowId: string, businessId?: string): Promise<ArLedgerDetailResponse> {
        try {
            const response = await api.get<ArLedgerDetailResponse>(`/accounting/ar/ledger/${ledgerRowId}`, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch AR ledger detail:', error);
            throw error;
        }
    },

    async getArWeeklyMetrics(businessId?: string): Promise<ArWeeklyMetricRecord[]> {
        try {
            const response = await api.get<ArWeeklyMetricRecord[]>('/accounting/ar/weekly-metrics', {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch AR weekly metrics:', error);
            throw error;
        }
    },

    async getArWeeklyActions(params?: { business_id?: string; week_start?: string; status?: string; page?: number; page_size?: number }): Promise<PaginatedResponse<ArWeeklyActionRecord>> {
        try {
            const response = await api.get<PaginatedResponse<ArWeeklyActionRecord>>('/accounting/ar/weekly-actions', {
                params: {
                    business_id: params?.business_id,
                    week_start: params?.week_start,
                    status: params?.status,
                    page: params?.page ?? 1,
                    page_size: params?.page_size ?? 5,
                },
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch AR weekly actions:', error);
            throw error;
        }
    },

    async getArWeeklyRevenueContributions(params?: { business_id?: string; week_start?: string; included_in_weekly_revenue?: boolean; page?: number; page_size?: number }): Promise<PaginatedResponse<ArWeeklyRevenueContributionRecord>> {
        try {
            const response = await api.get<PaginatedResponse<ArWeeklyRevenueContributionRecord>>('/accounting/ar/weekly-revenue-contributions', {
                params: {
                    business_id: params?.business_id,
                    week_start: params?.week_start,
                    included_in_weekly_revenue: params?.included_in_weekly_revenue,
                    page: params?.page ?? 1,
                    page_size: params?.page_size ?? 10,
                },
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch AR weekly revenue contributions:', error);
            throw error;
        }
    },

    async selectBillPaymentMatch(billId: string, payload: { transaction_id: string; payment_account_id?: string }, businessId?: string): Promise<BillPaymentMatchPayload['match']> {
        try {
            const response = await api.post<BillPaymentMatchPayload['match']>(`/accounting/ledger/bills/${billId}/payment-match/select`, {
                transaction_id: payload.transaction_id,
                payment_account_id: payload.payment_account_id,
            }, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to select bill payment match:', error);
            throw error;
        }
    },

    async applyBillPaymentMatch(billId: string, payload: { transaction_id?: string; match_id?: string; payment_account_id?: string }, businessId?: string): Promise<any> {
        try {
            const response = await api.post(`/accounting/ledger/bills/${billId}/payment-match/apply`, payload, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to apply bill payment match:', error);
            throw error;
        }
    },

    async getQboBillById(billId: string, businessId?: string): Promise<QboBillRecord> {
        try {
            const response = await api.get<QboBillRecord>(`/accounting/ledger/qbo-bills/${billId}`, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch QBO bill by id:', error);
            throw error;
        }
    },

    async getQboInvoiceById(invoiceId: string, businessId?: string): Promise<QboInvoiceRecord> {
        try {
            const response = await api.get<QboInvoiceRecord>(`/accounting/ledger/qbo-invoices/${invoiceId}`, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch QBO invoice by id:', error);
            throw error;
        }
    },

    async getQboTransactionById(transactionId: string, businessId?: string): Promise<QboTransactionListItem> {
        try {
            const response = await api.get<QboTransactionListItem>(`/accounting/ledger/qbo-transactions/${transactionId}`, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch QBO transaction by id:', error);
            throw error;
        }
    },

    async getArManualLinkCandidates(ledgerRowId: string, params?: { business_id?: string; search?: string; payment_scope?: 'customer' | 'all'; limit?: number }): Promise<ArManualLinkCandidatesResponse> {
        try {
            const response = await api.get<ArManualLinkCandidatesResponse>(`/accounting/ar/ledger/${ledgerRowId}/manual-link-candidates`, {
                params: {
                    business_id: params?.business_id,
                    search: params?.search,
                    payment_scope: params?.payment_scope,
                    limit: params?.limit,
                },
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch AR manual link candidates:', error);
            throw error;
        }
    },

    async applyArManualLink(ledgerRowId: string, payload: ArManualLinkApplyRequest, businessId?: string): Promise<ArManualLinkApplyResponse> {
        try {
            const response = await api.post<ArManualLinkApplyResponse>(`/accounting/ar/ledger/${ledgerRowId}/manual-link`, payload, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to apply AR manual link:', error);
            throw error;
        }
    },

    async applyArManualAllocation(ledgerRowId: string, payload: ArManualAllocationApplyRequest, businessId?: string): Promise<ArManualAllocationApplyResponse> {
        try {
            const response = await api.post<ArManualAllocationApplyResponse>(`/accounting/ar/ledger/${ledgerRowId}/allocation`, payload, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to apply AR manual allocation:', error);
            throw error;
        }
    },

    async applyArManualInvoiceAllocation(ledgerRowId: string, payload: ArManualInvoiceAllocationApplyRequest, businessId?: string): Promise<any> {
        try {
            const response = await api.post(`/accounting/ar/ledger/${ledgerRowId}/invoice-allocation`, payload, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to apply AR invoice allocation:', error);
            throw error;
        }
    },

    async removeArManualLink(ledgerRowId: string, businessId?: string): Promise<ArManualLinkApplyResponse> {
        try {
            const response = await api.delete<ArManualLinkApplyResponse>(`/accounting/ar/ledger/${ledgerRowId}/manual-link`, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to remove AR manual link:', error);
            throw error;
        }
    },

    async removeArLinkedEvidence(
        ledgerRowId: string,
        payload: { linked_source_kind: 'bank' | 'bank_receipt' | 'payment' | 'qbo_payment' | 'invoice' | 'ar_invoice'; linked_source_id: string },
        businessId?: string
    ): Promise<ArManualLinkApplyResponse> {
        try {
            const response = await api.delete<ArManualLinkApplyResponse>(`/accounting/ar/ledger/${ledgerRowId}/linked-evidence`, {
                params: {
                    business_id: businessId,
                    linked_source_kind: payload.linked_source_kind,
                    linked_source_id: payload.linked_source_id,
                },
            });
            return response.data;
        } catch (error) {
            console.error('Failed to remove AR linked evidence:', error);
            throw error;
        }
    },
};

export default ledgerViewsApi;
